// Where a sticky note dropped onto the Lesson content sits (LearnNowPage
// CanvasSticky). The lesson area's size changes — lessons sidebar, chat pane,
// zoom, window — so the horizontal position is stored as a fraction of the
// free width (`xr`, 0 = left edge, 1 = right edge) instead of raw pixels:
// a note placed at an edge stays at that edge. Every placement is kept fully
// inside the area INCLUDING a rotated note's corners, which used to stick
// out and scroll the whole lesson sideways.
//
// canvasPos shapes: { xr, y } (current) or { x, y } (pixels, saved before
// this fix — still read, converted on the next move).

export const STICKY_SIZE = 180;

/** How far a square note's corners reach past its box when rotated by `deg`. */
export function rotatedOverhang(deg = 0) {
  const r = ((deg || 0) * Math.PI) / 180;
  return Math.max(0, (STICKY_SIZE * (Math.abs(Math.cos(r)) + Math.abs(Math.sin(r))) - STICKY_SIZE) / 2);
}

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

/** The free range for the note's left/top edge on one axis ({start, range}); range <= 0 = no room. */
function axis(length, pad) {
  return { start: pad, range: (length || 0) - STICKY_SIZE - 2 * pad };
}

function placeY(y, area, pad) {
  const { start, range } = axis(area.height, pad);
  return range > 0 ? clamp(y || 0, start, start + range) : 0;
}

/** Pixel position (from a drop or a drag) → what gets saved. */
export function toStored(px, area, rotation = 0) {
  const pad = rotatedOverhang(rotation);
  const { start, range } = axis(area.width, pad);
  const xr = range > 0 ? clamp(((px.x || 0) - start) / range, 0, 1) : 0;
  return { xr, y: placeY(px.y, area, pad) };
}

/** Saved position → pixels for the current area size and rotation. */
export function toPixels(pos, area, rotation = 0) {
  const pad = rotatedOverhang(rotation);
  const { start, range } = axis(area.width, pad);
  let x = 0;
  if (range > 0) {
    x = typeof pos?.xr === 'number'
      ? start + clamp(pos.xr, 0, 1) * range
      : clamp(pos?.x || 0, start, start + range);
  }
  return { x, y: placeY(pos?.y, area, pad) };
}
